import React, { useState } from 'react';
import * as _ from 'lodash';
import useSWR from 'swr';
import { Dropdown, ModalHeader, Button, Box, Flex, Heading, Icon, Divider, Loader } from '@vibe/core';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { AppModal, AppModalContent, AppModalFooter } from '@gorilla/ui/src/components/AppModal';
import { Check, Alert } from '@vibe/icons';
import { TemplateBasic } from '@gorilla/spreadsheet-shared/src/lib/spreadsheet-manager/types';
import { exportTo } from '@gorilla/spreadsheet-shared/src/lib/spreadsheet-manager/spreadsheet-manager';
import { ErrorBox } from '@gorilla/ui/src/components/ErrorBox';
import { getTemplates } from '../services/templates';
import Loading from './Loading';
import { mondayClient } from '../services/monday';
import { useSpreadsheetLoader } from '../hooks/use-spreadsheet-loader';

interface ImportStepProps {
  template: TemplateBasic;
  mode: string;
  onClose: (data?: any) => void;
}

export function ImportStep({ template, mode, onClose }: ImportStepProps) {
  const { loadingMessage, data, error } = useSpreadsheetLoader(_.get(template, 'id'), 'luckysheet');

  function capitalizeFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  let statusIcon = <></>;

  if (error) {
    statusIcon = (
      <div className="statusChecked error">
        <Icon icon={Alert} size={10} />
      </div>
    );
  } else if (data) {
    statusIcon = (
      <div className="statusChecked">
        <Icon icon={Check} size={10} />
      </div>
    );
  } else {
    statusIcon = <Loader size="xs" color="primary" />;
  }

  return (
    <>
      <AppModalContent>
        <Box border={true} borderColor="layoutBorderColor" rounded="small">
          <>
            <Box padding="medium">
              <Flex gap={8}>
                <div>
                  <strong>Type:</strong> {capitalizeFirstLetter(mode)}{' '}
                  {mode === 'overwrite' ? 'worksheets with the same name' : 'worksheets to the spreadsheet'}
                </div>
                <div className="statusChecked">
                  <Icon icon={Check} size={10} />
                </div>
              </Flex>
            </Box>
            <Divider withoutMargin={true} />
            <Box padding="medium">
              <Flex gap={8}>
                <div>
                  <strong>Template:</strong> {template.name}
                </div>
                <div className="statusChecked">
                  <Icon icon={Check} size={10} />
                </div>
              </Flex>
            </Box>
            <Divider withoutMargin={true} />
            <Box padding="medium">
              <Flex gap={8}>
                <div>
                  <strong>Items:</strong> {error ? error.message : loadingMessage}
                </div>
                <div style={{ marginLeft: 'auto' }}>{statusIcon}</div>
              </Flex>
            </Box>
          </>
          {error ? <ErrorBox error={error} mondayClient={mondayClient} /> : null}
        </Box>
      </AppModalContent>
      <AppModalFooter>
        <Flex>
          <Box style={{ marginLeft: 'auto' }}>
            <Flex gap={12} justify="end">
              <Button
                kind="tertiary"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={!data}
                loading={!data && !error}
                onClick={async () => {
                  onClose(data || undefined);
                }}
              >
                Confirm
              </Button>
            </Flex>
          </Box>
        </Flex>
      </AppModalFooter>
    </>
  );
}

interface SelectionStepProps {
  templates: TemplateBasic[];
  templateId: number | null;
  mode: string;
  onTemplateIdChange: (newTemplateId: number) => void;
  onModeChange: (newMode: string) => void;
  onClose: () => void;
  onStepChange: (newStep: string) => void;
}

export function SelectionStep({
  templates,
  templateId,
  onTemplateIdChange,
  mode,
  onModeChange,
  onClose,
  onStepChange,
}: SelectionStepProps) {
  const templateOptions: { label: string; value: number }[] = _.orderBy(templates, 'name').map((template) => ({
    label: template.name,
    value: template.id,
  }));

  const selectedTemplateOption = templateOptions.find((option) => option.value === templateId);

  return (
    <>
      <AppModalContent>
        <div>
          <Flex gap={24}>
            <div
              className={mode === 'overwrite' ? 'multiTypeSelected multiType' : 'multiType'}
              onClick={() => {
                onModeChange('overwrite');
              }}
            >
              <div className="checkTypeIcon">
                <Icon icon={Check} size={16} />
              </div>
              <Heading type="h3">Overwrite worksheets</Heading>
              <div
                style={{
                  fontWeight: 300,
                  color: '#676879',
                }}
              >
                with the same name
              </div>
            </div>
            <div
              className={mode === 'append' ? 'multiTypeSelected multiType' : 'multiType'}
              onClick={() => {
                onModeChange('append');
              }}
            >
              <div className="checkTypeIcon">
                <Icon icon={Check} size={16} />
              </div>
              <Heading type="h3">Append worksheets</Heading>
              <div
                style={{
                  fontWeight: 300,
                  color: '#676879',
                }}
              >
                to the spreadsheet
              </div>
            </div>
          </Flex>
        </div>

        <Box paddingTop="medium">
          <Dropdown
            searchable
            onOptionSelect={(ev: any) => {
              onTemplateIdChange(ev.value);
            }}
            options={templateOptions}
            placeholder="Select template"
            value={dropdownValue(selectedTemplateOption)}
            clearable={false}
            size="large"
          />
        </Box>
      </AppModalContent>
      <AppModalFooter>
        <Flex>
          <div style={{ color: '#afb0bd' }}>Choose from the options above and select a template</div>
          <Box style={{ marginLeft: 'auto' }}>
            <Flex gap={12} justify="end">
              <Button
                kind="tertiary"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={!templateId}
                onClick={() => {
                  onStepChange('import');
                }}
              >
                Next
              </Button>
            </Flex>
          </Box>
        </Flex>
      </AppModalFooter>
    </>
  );
}

type ImportTemplateModalProps = {
  onClose: (result?: { data: ReturnType<typeof exportTo>; mode: string }) => void;
  show: boolean;
};

export function ImportTemplateModal({ show, onClose }: ImportTemplateModalProps) {
  const { data: templates, error } = useSWR('/api/get-templates', async () => getTemplates());
  const [mode, setMode] = useState('overwrite');
  const [step, setStep] = useState('selection');
  const [selectedTemplateId, setSelectedTemplateId] = useState(templates && templates.length ? templates[0].id : null);

  let content: React.ReactNode = null;

  if (!templates) {
    content = (
      <>
        <AppModalContent>
          <Box padding="medium" border={true} borderColor="layoutBorderColor" rounded="small">
            <Box padding="xxxl">
              <Loading />
            </Box>
          </Box>
        </AppModalContent>
      </>
    );
  } else {
    if (step === 'selection') {
      content = (
        <>
          <SelectionStep
            templates={templates}
            templateId={selectedTemplateId}
            onTemplateIdChange={(newTemplateId) => {
              setSelectedTemplateId(newTemplateId);
            }}
            mode={mode}
            onModeChange={(newMode) => {
              setMode(newMode);
            }}
            onClose={onClose}
            onStepChange={(newStep) => {
              setStep(newStep);
            }}
          />
        </>
      );
    } else if (step === 'import' && selectedTemplateId) {
      content = (
        <ImportStep
          template={templates.find((template) => template.id === selectedTemplateId) as TemplateBasic}
          mode={mode}
          onClose={(data) => {
            if (data) {
              onClose({ data, mode });
            } else {
              onClose();
            }
          }}
        />
      );
    }
  }

  return (
    <AppModal show={show} onClose={() => onClose()} alertDialog={false} data-testid="import-template-modal">
      <ModalHeader title={`Import from template`} />
      <div style={{ marginTop: '20px' }}>{content}</div>
    </AppModal>
  );
}
